// ---------------------------------------------------------------------------
// AlertRule wire schemas (TASK-042 / §6.3.6).
//
// MVP supports exactly THREE alert types per tasks.json TASK-042 +
// meta.architectural_resolutions.alert_types_mvp:
//   * HEALTH_DROP   — community Health Score crossed BELOW threshold this week
//   * MEMBER_DROP   — member count fell by more than `threshold` (fraction)
//                     in the last 24h
//   * ACTIVITY_SPIKE — today's messages exceed median(last 14 days) × threshold
//
// ADMIN_SILENT, ACTIVITY_DROP, NEW_MEMBER_SURGE remain in the Prisma enum
// (schema.prisma cannot be re-narrowed without a migration churn that buys
// nothing) but the API rejects them at the boundary so the user-visible
// surface is the supported subset only. TASK-043 backend evaluators read the
// same `alertTypeMvpSchema` so a future alert-rule row hand-inserted into
// Postgres with type=ADMIN_SILENT would be ignored by the dispatcher rather
// than crashing it.
// ---------------------------------------------------------------------------
import { z } from "zod";
/** The three alert types supported in MVP. Authoritative for the wire. */
export const ALERT_TYPES_MVP = [
    "HEALTH_DROP",
    "MEMBER_DROP",
    "ACTIVITY_SPIKE",
];
export const alertTypeMvpSchema = z.enum(ALERT_TYPES_MVP);
/**
 * §6.3.6 sensible defaults per AlertType. The defaults are chosen so an owner
 * can enable a rule with one tap and get reasonable behavior; tuning is
 * supported via the threshold input in the Mini App.
 *
 * - HEALTH_DROP: 50 — the §6.3.5 boundary between "Needs Attention" (yellow)
 *   and "Good" (blue). Crossing below this within a week is the canonical
 *   "something is going wrong" trigger.
 * - MEMBER_DROP: 0.10 — losing 10% of members in 24h. Above the noise floor
 *   for any non-trivial community; below it, day-to-day churn would page.
 * - ACTIVITY_SPIKE: 1.5 — a 1.5× multiplier of the 14-day median. Captures
 *   sudden activity (raid, viral post) without firing on weekly cycles.
 */
export const ALERT_RULE_DEFAULTS = {
    HEALTH_DROP: { threshold: 50, windowHours: 24, cooldownHours: 24 },
    MEMBER_DROP: { threshold: 0.1, windowHours: 24, cooldownHours: 24 },
    ACTIVITY_SPIKE: { threshold: 1.5, windowHours: 24, cooldownHours: 24 },
};
/**
 * Threshold ranges per type, enforced at API boundary. Lower bound INCLUSIVE
 * to allow "any drop" rules; upper bound INCLUSIVE so a user can configure
 * "100" for HEALTH_DROP (effectively "fire whenever score is below 100",
 * i.e. always — useful for testing the dispatcher path).
 */
export const ALERT_THRESHOLD_RANGES = {
    HEALTH_DROP: { min: 0, max: 100 },
    MEMBER_DROP: { min: 0, max: 1 },
    ACTIVITY_SPIKE: { min: 1, max: 10 },
};
/**
 * Window / cooldown bounds — same for every type. Cooldown 1h..168h (1h..7d)
 * keeps a runaway rule from spamming and a too-quiet rule from missing
 * sustained issues.
 */
export const ALERT_WINDOW_HOURS_MIN = 1;
export const ALERT_WINDOW_HOURS_MAX = 168;
export const ALERT_COOLDOWN_HOURS_MIN = 1;
export const ALERT_COOLDOWN_HOURS_MAX = 168;
export function validateThresholdForType(type, threshold) {
    if (!Number.isFinite(threshold))
        return "threshold_not_finite";
    const range = ALERT_THRESHOLD_RANGES[type];
    if (threshold < range.min)
        return "threshold_below_min";
    if (threshold > range.max)
        return "threshold_above_max";
    return null;
}
export function getDefaultAlertRule(type) {
    return { ...ALERT_RULE_DEFAULTS[type] };
}
/**
 * Wire shape for one AlertRule row. `lastTriggeredAt` is null until the
 * dispatcher (TASK-043) fires the rule for the first time. Timestamps are
 * ISO 8601 strings — same convention as every other entity in shared/.
 */
export const alertRuleResponseSchema = z.object({
    id: z.string().min(1),
    communityId: z.string().min(1),
    type: alertTypeMvpSchema,
    threshold: z.number().finite(),
    windowHours: z.number().int().positive(),
    enabled: z.boolean(),
    cooldownHours: z.number().int().positive(),
    lastTriggeredAt: z.string().datetime().nullable(),
    createdAt: z.string().datetime(),
    updatedAt: z.string().datetime(),
});
/**
 * POST body for creating a rule. `windowHours` / `cooldownHours` / `enabled`
 * are optional — the route fills them from `ALERT_RULE_DEFAULTS` when absent.
 * Threshold IS required because it is the single user-visible knob; defaulting
 * it silently would hide a UI bug.
 */
export const createAlertRuleRequestSchema = z
    .object({
    type: alertTypeMvpSchema,
    threshold: z.number().finite(),
    windowHours: z
        .number()
        .int()
        .min(ALERT_WINDOW_HOURS_MIN)
        .max(ALERT_WINDOW_HOURS_MAX)
        .optional(),
    cooldownHours: z
        .number()
        .int()
        .min(ALERT_COOLDOWN_HOURS_MIN)
        .max(ALERT_COOLDOWN_HOURS_MAX)
        .optional(),
    enabled: z.boolean().optional(),
})
    .strict();
/**
 * PATCH body. Type is IMMUTABLE — to change type, delete + create. This
 * keeps the dispatcher's lastTriggeredAt cooldown aligned with the
 * (communityId, type) pair the user actually expected to fire.
 */
export const updateAlertRuleRequestSchema = z
    .object({
    threshold: z.number().finite().optional(),
    windowHours: z
        .number()
        .int()
        .min(ALERT_WINDOW_HOURS_MIN)
        .max(ALERT_WINDOW_HOURS_MAX)
        .optional(),
    cooldownHours: z
        .number()
        .int()
        .min(ALERT_COOLDOWN_HOURS_MIN)
        .max(ALERT_COOLDOWN_HOURS_MAX)
        .optional(),
    enabled: z.boolean().optional(),
})
    .strict()
    .refine((v) => v.threshold !== undefined ||
    v.windowHours !== undefined ||
    v.cooldownHours !== undefined ||
    v.enabled !== undefined, {
    message: "PATCH body must contain at least one updatable field",
    path: [],
});
/**
 * Test-fire response. `wouldFire` is the canonical answer ("yes / no");
 * `metricValue` and `comparator` provide the underlying signal so the Mini
 * App can render a richer explanation ("score 42 < threshold 50").
 *
 * `reason` is a code (not a free-text message) so the Mini App owns
 * localization. Possible codes:
 *   - "would_fire"
 *   - "would_not_fire"
 *   - "insufficient_history"  — for HEALTH_DROP without a 7d-ago reference
 *   - "insufficient_data"     — for MEMBER_DROP / ACTIVITY_SPIKE w/o baseline
 *   - "rule_disabled"         — preview never fires for a disabled rule
 */
export const ALERT_TEST_FIRE_REASONS = [
    "would_fire",
    "would_not_fire",
    "insufficient_history",
    "insufficient_data",
    "rule_disabled",
];
export const alertTestFireReasonSchema = z.enum(ALERT_TEST_FIRE_REASONS);
export const alertTestFireResponseSchema = z.object({
    wouldFire: z.boolean(),
    reason: alertTestFireReasonSchema,
    type: alertTypeMvpSchema,
    threshold: z.number().finite(),
    /** The current metric value used for the comparison; null on insufficient_*. */
    metricValue: z.number().finite().nullable(),
    /**
     * Human-readable secondary value useful for the Mini App ("ref score 7d
     * ago: 60", "messages today: 200 vs median 100"). Optional so the
     * frontend renders an explanation only when the type provides one.
     */
    referenceValue: z.number().finite().nullable(),
    generatedAt: z.string().datetime(),
});
//# sourceMappingURL=alert-rules.js.map